import { useEffect, useRef, useState } from 'react';
import { fetchMaintenanceConfig } from 'utils/services/fetchMaintenanceConfig';

// Jitter matters here specifically: once this ships across many merchant
// domains, they must not all poll in lockstep every 5 minutes.
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const POLL_JITTER_MS = 60 * 1000;

const FAIL_OPEN_CONFIG = null; // null = "no override, use the baked REACT_APP_SERVER_* defaults"

/**
 * Polls the maintenance toggle published to the assets bucket, so ops can
 * flip maintenance mode - fleet-wide or for one merchant host - with a file
 * write instead of a rebuild and redeploy. See
 * docs/NETLIFY_MIGRATION.md, §10, and scripts/maintenance.sh.
 *
 * @returns {null | { serverUp?: boolean, uptime?: string, message?: string,
 *   hostOverrides?: Record<string, { serverUp?: boolean, uptime?: string, message?: string }> }}
 */
export function useMaintenanceConfig() {
  const [config, setConfig] = useState(FAIL_OPEN_CONFIG);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    async function check() {
      const remoteConfig = await fetchMaintenanceConfig();
      if (mountedRef.current) setConfig(remoteConfig);
    }

    check();

    const interval = setInterval(
      check,
      POLL_INTERVAL_MS + Math.random() * POLL_JITTER_MS,
    );

    function handleVisibility() {
      if (document.visibilityState === 'visible') check();
    }
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return config;
}
